import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, Button, Dialog, IconButton, Typography } from "@mui/material";
import { useState } from "react";
import { useArchiveUser } from "@/hooks/useUsers";
import type { User } from "@/interfaces/user";

interface OrgUserDeleteDialogProps {
  user: User | null;
  onClose: () => void;
}

/**
 * Delete confirmation for Account → Users, matching the reference demo's csUserDelOverlay
 * exactly (copy + Cancel/Delete user buttons). Reuses the existing archive-via-Clerk flow
 * (useArchiveUser → DELETE /admin/users/:id → Clerk delete → webhook marks archived) — the
 * campaign reassignment described in the copy happens server-side, before the Clerk call,
 * as part of that same existing endpoint.
 */
export default function OrgUserDeleteDialog({ user, onClose }: OrgUserDeleteDialogProps) {
  const [error, setError] = useState<string | null>(null);
  const { mutate: archive, isPending } = useArchiveUser();

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleDelete = () => {
    if (!user) return;
    setError(null);
    archive(user.id, {
      onSuccess: handleClose,
      onError: (err) => setError(err instanceof Error ? err.message : "Failed to delete user."),
    });
  };

  return (
    <Dialog
      open={!!user}
      onClose={isPending ? undefined : handleClose}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            borderRadius: "12px",
            p: "32px",
            width: 480,
            maxWidth: "calc(100vw - 40px)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
            position: "relative",
          },
        },
      }}
    >
      <IconButton
        onClick={handleClose}
        disabled={isPending}
        aria-label="Close"
        sx={{
          position: "absolute",
          top: 20,
          right: 20,
          border: "1px solid var(--color-smoke)",
          borderRadius: "var(--radius-btn)",
          color: "var(--color-graphite)",
          width: 36,
          height: 36,
          "&:hover": { bgcolor: "var(--color-wax)", color: "var(--color-rodeo-black)" },
        }}
      >
        <CloseIcon sx={{ fontSize: 20 }} />
      </IconButton>

      <Typography
        sx={{
          fontFamily: "var(--font-crimson)",
          fontSize: "24px",
          fontWeight: 400,
          color: "var(--color-rodeo-black)",
          mb: "18px",
        }}
      >
        Delete user
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: "10px",
          bgcolor: "var(--color-wax)",
          borderRadius: "10px",
          p: "12px 14px",
          mb: "8px",
        }}
      >
        <WarningAmberIcon
          sx={{ fontSize: "20px", color: "var(--color-dark-dust)", flexShrink: 0 }}
        />
        <Typography sx={{ fontSize: "13.5px", color: "var(--color-rodeo-black)", lineHeight: 1.5 }}>
          Deleting this user will move any of the campaigns they are managing to the main account
          admin. Are you sure you want to proceed?
        </Typography>
      </Box>

      {error && (
        <Typography
          sx={{
            mt: "8px",
            fontSize: "13px",
            color: "var(--color-good-wine)",
            bgcolor: "var(--color-good-wine-bg)",
            border: "1px solid var(--color-good-wine-4)",
            borderRadius: "8px",
            p: "10px 12px",
          }}
        >
          {error}
        </Typography>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "8px", mt: "22px" }}>
        <Button
          onClick={handleClose}
          disabled={isPending}
          disableRipple
          sx={{
            fontFamily: "var(--font-lato)",
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "1.5px",
            textTransform: "uppercase",
            color: "var(--color-rodeo-black)",
            bgcolor: "var(--color-white)",
            border: "1px solid var(--color-smoke)",
            borderRadius: "6px",
            px: "20px",
            py: "10px",
            "&:hover": { borderColor: "var(--color-dark-dust)", bgcolor: "var(--color-white)" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          disabled={isPending}
          disableRipple
          disableElevation
          sx={{
            fontFamily: "var(--font-lato)",
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "1.5px",
            textTransform: "uppercase",
            bgcolor: "var(--color-good-wine)",
            color: "var(--color-white)",
            borderRadius: "6px",
            px: "20px",
            py: "10px",
            "&:hover": { bgcolor: "var(--color-good-wine-2)" },
            "&.Mui-disabled": { opacity: 0.45, color: "var(--color-white)" },
          }}
        >
          {isPending ? "Deleting…" : "Delete user"}
        </Button>
      </Box>
    </Dialog>
  );
}
